// F12 (reset brief 16): chapter logic for the player, kept pure so it is unit-tested without a
// video element. Chapters come from `GET /grackles/{id}/chapters` (manual, and generated once
// the F1 pipeline lands). Resume stays local (FR-EN7); nothing here talks to the server.

export interface Chapter {
  id: string;
  start_ms: number;
  title: string;
  source: "manual" | "generated";
  added_by: { id: string; name: string } | null;
  model_id: string | null;
  provenance: string | null;
}

export interface ChaptersResponse {
  chapters: Chapter[];
  can_edit: boolean;
}

/** Shift+← within this many seconds of a chapter start goes to the chapter before it. */
export const PREV_GRACE_S = 2;

function sorted(chapters: readonly Chapter[]): Chapter[] {
  return [...chapters].sort((a, b) => a.start_ms - b.start_ms);
}

/** Index of the chapter playing at `seconds` (the last one starting at or before it), or -1. */
export function chapterIndexAt(chapters: readonly Chapter[], seconds: number): number {
  const ms = seconds * 1000;
  let idx = -1;
  sorted(chapters).forEach((c, i) => {
    if (c.start_ms <= ms) idx = i;
  });
  return idx;
}

/** The chapter playing at `seconds` (same rule as `chapterIndexAt`), or null before the first
 * one starts — used by the player's fullscreen overlay (brief scope 3) to show its title. */
export function chapterAt(chapters: readonly Chapter[], seconds: number): Chapter | null {
  const idx = chapterIndexAt(chapters, seconds);
  return idx === -1 ? null : sorted(chapters)[idx];
}

/** Start (seconds) of the next chapter after `seconds`, or null at the last one. */
export function nextChapterStart(chapters: readonly Chapter[], seconds: number): number | null {
  const next = sorted(chapters).find((c) => c.start_ms / 1000 > seconds + 0.001);
  return next ? next.start_ms / 1000 : null;
}

/** Start (seconds) of the current chapter, or of the previous one when already near the start. */
export function prevChapterStart(chapters: readonly Chapter[], seconds: number): number | null {
  const list = sorted(chapters).filter((c) => c.start_ms / 1000 < seconds - PREV_GRACE_S);
  const prev = list[list.length - 1];
  return prev ? prev.start_ms / 1000 : list.length === 0 && chapters.length > 0 ? 0 : null;
}

/** DS ChapterBar segments: one per chapter (plus a lead-in before the first), widths summing to 1. */
export function chapterSegments(
  chapters: readonly Chapter[],
  durationS: number,
  currentS: number,
): { width: number; progress: number; title?: string }[] {
  const dur = durationS * 1000;
  const list = sorted(chapters).filter((c) => !(dur > 0) || c.start_ms < dur);
  if (!(dur > 0) || list.length === 0) return [];
  const starts = list.map((c) => c.start_ms);
  const titles: (string | undefined)[] = list.map((c) => c.title);
  if (starts[0] > 0) {
    starts.unshift(0);
    titles.unshift(undefined);
  }
  const cur = currentS * 1000;
  return starts.map((s, i) => {
    const end = i + 1 < starts.length ? starts[i + 1] : dur;
    const len = Math.max(end - s, 0);
    const progress = len === 0 ? 0 : Math.min(Math.max((cur - s) / len, 0), 1);
    return { width: len / dur, progress, title: titles[i] };
  });
}

/**
 * `?t=` deep link → seconds. Accepts `90`, `90s`, `1m30s`, `1h2m3s`, `1:30` and `1:02:03`.
 * Anything else is null (ignored, never an error).
 */
export function parseDeepLinkT(value: string | null | undefined): number | null {
  if (!value) return null;
  const v = value.trim();
  if (/^\d+(\.\d+)?s?$/.test(v)) return parseFloat(v);
  const hms = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(v);
  if (hms && (hms[1] || hms[2] || hms[3])) {
    return (+(hms[1] ?? 0)) * 3600 + (+(hms[2] ?? 0)) * 60 + (+(hms[3] ?? 0));
  }
  const colon = /^(?:(\d+):)?(\d{1,2}):(\d{2})$/.exec(v);
  if (colon) return (+(colon[1] ?? 0)) * 3600 + +colon[2] * 60 + +colon[3];
  return null;
}

/** "Chapters generated by <model> via <account>" / "Added by <name>" (brief scope 5). */
export function provenanceLine(c: Chapter): string | null {
  if (c.source === "manual") return c.added_by ? `Added by ${c.added_by.name}` : null;
  if (!c.model_id) return null;
  const via =
    c.provenance === "personal" ? "your own account" : c.provenance === "hosted" ? "Grackle hosted" : "your organization";
  return `Chapters generated by ${c.model_id} via ${via}`;
}

/** Caption download links (brief scope 4); `share` keeps share-link viewers authorized. */
export function captionsUrl(apiBase: string, grackleId: string, format: "vtt" | "srt", share?: string | null): string {
  const q = share ? `?share=${encodeURIComponent(share)}` : "";
  return `${apiBase}/grackles/${grackleId}/captions/${format}${q}`;
}
